import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Grid,
    Card,
    CardContent,
    Paper,
    CircularProgress,
    Alert,
    SelectChangeEvent,
    OutlinedInput,
    Chip,
    Checkbox,
    ListItemText,
} from '@mui/material';
import { PieChart, LineChart } from '@mui/x-charts';
import { getFlashBetSports, getCategoriesBySports, getFlashBetOverviewStats } from '../Api';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import FlashBetOverviewStatsDto from '../models/dtos/FlashBetOverviewStatsDto';
import StatsInterval from '../models/dtos/StatsInterval';
import SportPieChartDataDto from '../models/dtos/SportPieChartDataDto';
import DailyGamesChartDataDto from '../models/dtos/DailyGamesChartDataDto';
import DailyUsersChartDataDto from '../models/dtos/DailyUsersChartDataDto';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

// Color palettes for charts
const SPORTS_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];
const BUYIN_PAYOUT_COLORS = ['#FF8042', '#00C49F'];
const GAMES_COLORS = {
    totalGames: '#0088FE',
    totalWonGames: '#00C49F', 
    totalLostGames: '#FF8042',
    totalExpiredGames: '#FFBB28'
};

const FlashBetStatsOverview: React.FC = () => {
    // State management
    const [allSports, setAllSports] = useState<FlashBetSportDto[]>([]);
    const [availableCategories, setAvailableCategories] = useState<FlashBetCategoryDto[]>([]);
    const [selectedSportIds, setSelectedSportIds] = useState<number[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
    const [selectedInterval, setSelectedInterval] = useState<StatsInterval>(StatsInterval.SEVEN_DAYS);
    
    const [statsData, setStatsData] = useState<FlashBetOverviewStatsDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [statsLoading, setStatsLoading] = useState<boolean>(false);
    
    // Error handling
    const [error, setError] = useState<string | null>(null);

    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    // Load categories when selected sports change
    useEffect(() => {
        loadCategoriesForSports();
    }, [selectedSportIds]);

    // Load stats when parameters change
    useEffect(() => {
        loadStats();
    }, [selectedSportIds, selectedCategoryIds, selectedInterval]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const sportsData = await getFlashBetSports();
            setAllSports(sportsData);
            
            // Auto-select all sports
            const sportIds = sportsData.map(sport => sport.id);
            setSelectedSportIds(sportIds);
            
        } catch (error) {
            console.error('Error loading initial data:', error);
            setError('Failed to load sports data');
        } finally {
            setLoading(false);
        }
    };

    const loadCategoriesForSports = async () => {
        if (selectedSportIds.length === 0) {
            setAvailableCategories([]);
            setSelectedCategoryIds([]);
            return;
        }

        try {
            const categoriesData = await getCategoriesBySports(selectedSportIds);
            setAvailableCategories(categoriesData);
            
            // Auto-select all categories
            const categoryIds = categoriesData.map(category => category.id);
            setSelectedCategoryIds(categoryIds);
            
        } catch (error) {
            console.error('Error loading categories:', error);
            setError('Failed to load categories data');
        }
    };

    const loadStats = async () => {
        try {
            setStatsLoading(true);
            setError(null);
            
            const stats = await getFlashBetOverviewStats(selectedInterval, selectedSportIds, selectedCategoryIds);
            setStatsData(stats);
            
        } catch (error) {
            console.error('Error loading stats:', error);
            setError('Failed to load statistics data');
        } finally {
            setStatsLoading(false);
        }
    };

    const handleSportsChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value as number[];
        setSelectedSportIds(value);
    };

    const handleCategoriesChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as string[];
        setSelectedCategoryIds(value);
    };

    const handleIntervalChange = (event: SelectChangeEvent<StatsInterval>) => {
        setSelectedInterval(event.target.value as StatsInterval);
    };

    const handleSelectAllSports = () => {
        // Always select ALL sports, including disabled ones
        const sportIds = allSports.map(sport => sport.id);
        setSelectedSportIds(sportIds);
    };

    const handleSelectAllCategories = () => {
        // Always select ALL categories, including disabled ones
        const categoryIds = availableCategories.map(category => category.id);
        setSelectedCategoryIds(categoryIds);
    };

    const handleResetSports = () => {
        setSelectedSportIds([]);
    };

    const handleResetCategories = () => {
        setSelectedCategoryIds([]);
    };

    const getSelectedSportsDisplay = () => {
        if (selectedSportIds.length === 0) return 'None selected';
        return selectedSportIds.map(id => {
            const sport = allSports.find(s => s.id === id);
            return sport ? sport.name : `ID: ${id}`;
        }).join(', ');
    };

    const getSelectedCategoriesDisplay = () => {
        if (selectedCategoryIds.length === 0) return 'None selected';
        return selectedCategoryIds.map(id => {
            const category = availableCategories.find(c => c.id === id);
            return category ? category.name : `ID: ${id}`;
        }).join(', ');
    };

    const formatBuyInPayoutData = () => {
        if (!statsData?.buyInVsPayoutPieChartData) return [];
        
        return [
            { name: 'Total Buy-In', value: statsData.buyInVsPayoutPieChartData.totalBuyIn },
            { name: 'Total Payout', value: statsData.buyInVsPayoutPieChartData.totalPayout },
        ];
    };

    const formatDailyGamesData = () => {
        if (!statsData?.dailyGamesLineChartData) return [];
        
        return statsData.dailyGamesLineChartData.map(item => ({
            date: new Date(item.date).toLocaleDateString(),
            'Total Games': item.totalGames,
            'Won Games': item.totalWonGames,
            'Lost Games': item.totalLostGames,
            'Expired Games': item.totalExpiredGames,
        }));
    };

    const formatDailyUsersData = () => {
        if (!statsData?.dailyUsersLineChartData) return [];
        
        return statsData.dailyUsersLineChartData.map(item => ({
            date: new Date(item.date).toLocaleDateString(),
            'Unique Users': item.uniqueUsersCount,
        }));
    };

    const getIntervalDisplayName = (interval: StatsInterval) => {
        switch (interval) {
            case StatsInterval.TODAY: return 'Today';
            case StatsInterval.SEVEN_DAYS: return 'Last 7 Days';
            case StatsInterval.THIRTY_DAYS: return 'Last 30 Days';
            case StatsInterval.NINETY_DAYS: return 'Last 90 Days';
            default: return interval;
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                FlashBet Statistics Overview
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>Filters</Typography>
                
                <Grid container spacing={3}>
                    {/* Time Interval */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Time Period</InputLabel>
                            <Select
                                value={selectedInterval}
                                label="Time Period"
                                onChange={handleIntervalChange}
                            >
                                {Object.values(StatsInterval).map(interval => (
                                    <MenuItem key={interval} value={interval}>
                                        {getIntervalDisplayName(interval)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Sports Selection */}
                    <Grid item xs={12}>
                        <Box>
                            <Box display="flex" gap={1} mb={1}>
                                <Button 
                                    size="small" 
                                    onClick={handleSelectAllSports}
                                    disabled={allSports.length === 0 || selectedSportIds.length === allSports.length}
                                >
                                    Select All Sports
                                </Button>
                                <Button 
                                    size="small" 
                                    onClick={handleResetSports}
                                    disabled={selectedSportIds.length === 0}
                                >
                                    Reset Sports
                                </Button>
                            </Box>
                            
                            <FormControl fullWidth>
                                <InputLabel>Sports</InputLabel>
                                <Select
                                    multiple
                                    value={selectedSportIds}
                                    onChange={handleSportsChange}
                                    input={<OutlinedInput label="Sports" />}
                                    renderValue={() => getSelectedSportsDisplay()}
                                    MenuProps={MenuProps}
                                >
                                    {allSports.map(sport => (
                                        <MenuItem key={sport.id} value={sport.id}>
                                            <Checkbox checked={selectedSportIds.includes(sport.id)} />
                                            <ListItemText primary={sport.name} />
                                            {sport.enabled === false && <Chip label="Disabled" size="small" color="warning" />}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>

                    {/* Categories Selection */}
                    <Grid item xs={12}>
                        <Box>
                            <Box display="flex" gap={1} mb={1}>
                                <Button 
                                    size="small" 
                                    onClick={handleSelectAllCategories}
                                    disabled={availableCategories.length === 0 || selectedCategoryIds.length === availableCategories.length}
                                >
                                    Select All Categories
                                </Button>
                                <Button 
                                    size="small" 
                                    onClick={handleResetCategories}
                                    disabled={selectedCategoryIds.length === 0}
                                >
                                    Reset Categories
                                </Button>
                            </Box>
                            
                            <FormControl fullWidth>
                                <InputLabel>Categories</InputLabel>
                                <Select
                                    multiple
                                    value={selectedCategoryIds}
                                    onChange={handleCategoriesChange}
                                    input={<OutlinedInput label="Categories" />}
                                    renderValue={() => getSelectedCategoriesDisplay()}
                                    MenuProps={MenuProps}
                                    disabled={availableCategories.length === 0}
                                >
                                    {availableCategories.map(category => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Checkbox checked={selectedCategoryIds.includes(category.id)} />
                                            <ListItemText primary={category.name} />
                                            {category.enabled === false && <Chip label="Disabled" size="small" color="warning" />}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Stats Loading */}
            {statsLoading && (
                <Box display="flex" justifyContent="center" mb={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Overview Stats */}
            {statsData && !statsLoading && (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={2.4}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary">
                                        {statsData.totalUsersPlayed.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Users Played
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary">
                                        {statsData.totalPlayedGames.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Games Played
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main">
                                        {statsData.totalWonGames.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Games Won
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="error.main">
                                        {statsData.totalLostGames.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Games Lost
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={2.4}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h4" color="warning.main">
                                        {statsData.totalExpiredGames.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Games Expired
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Charts Section */}
                    <Grid container spacing={4}>
                        {/* Sports Distribution Pie Chart */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Games Distribution by Sports
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 400 }}>
                                        <PieChart
                                            series={[{
                                                data: statsData.sportsPieChartData.map((item, index) => ({
                                                    id: index,
                                                    value: item.totalGames,
                                                    label: item.sportName,
                                                    color: SPORTS_COLORS[index % SPORTS_COLORS.length]
                                                }))
                                            }]}
                                            width={500}
                                            height={400}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Buy-in vs Payout Pie Chart */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Buy-in vs Payout Distribution
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 400 }}>
                                        <PieChart
                                            series={[{
                                                data: formatBuyInPayoutData().map((item, index) => ({
                                                    id: index,
                                                    value: item.value,
                                                    label: item.name,
                                                    color: BUYIN_PAYOUT_COLORS[index % BUYIN_PAYOUT_COLORS.length]
                                                }))
                                            }]}
                                            width={500}
                                            height={400}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Daily Games Line Chart */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Daily Games Trend
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 400 }}>
                                        <LineChart
                                            xAxis={[{ 
                                                scaleType: 'point', 
                                                data: formatDailyGamesData().map(item => item.date)
                                            }]}
                                            series={[
                                                {
                                                    data: formatDailyGamesData().map(item => item['Total Games']),
                                                    label: 'Total Games',
                                                    color: GAMES_COLORS.totalGames
                                                },
                                                {
                                                    data: formatDailyGamesData().map(item => item['Won Games']),
                                                    label: 'Won Games',
                                                    color: GAMES_COLORS.totalWonGames
                                                },
                                                {
                                                    data: formatDailyGamesData().map(item => item['Lost Games']),
                                                    label: 'Lost Games',
                                                    color: GAMES_COLORS.totalLostGames
                                                },
                                                {
                                                    data: formatDailyGamesData().map(item => item['Expired Games']),
                                                    label: 'Expired Games',
                                                    color: GAMES_COLORS.totalExpiredGames
                                                }
                                            ]}
                                            width={1000}
                                            height={400}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Daily Users Line Chart */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Daily Unique Users Trend
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 400 }}>
                                        <LineChart
                                            xAxis={[{ 
                                                scaleType: 'point', 
                                                data: formatDailyUsersData().map(item => item.date)
                                            }]}
                                            series={[
                                                {
                                                    data: formatDailyUsersData().map(item => item['Unique Users']),
                                                    label: 'Unique Users',
                                                    color: '#0088FE'
                                                }
                                            ]}
                                            width={1000}
                                            height={400}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* No Data Message */}
            {!statsData && !statsLoading && !error && (
                <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="textSecondary">
                        No statistics data available for the selected filters.
                    </Typography>
                </Box>
            )}

            {/* Bottom spacing */}
            <Box sx={{ mb: 10 }} />
        </Container>
    );
};

export default FlashBetStatsOverview;